import * as React from 'react';

import { AssessmentSectionId, UserRightsEnum } from '../../../utilities/Constants';

import { Accordion } from 'react-bootstrap';
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext';
import { AssessmentAccordionItemWrapper } from './AssessmentAccordionItemWrapper';
import Authorization from '../../../stores/Authorization';
import { BestFitCases } from './BestFitCases';
import { CaseStatistics } from './CaseStatistics';
import Common from '../../../stores/Common';
import { PredictedOutcome } from './PredictedOutcome';
import { ThreatScore } from './ThreatScore';
import { isArray } from 'lodash';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useCaseFileContext } from '../../../contexts/useCaseFileContext';
import { useGetAssessmentHasModel } from './data/useGetAssessmentHasModel';
import { useZoneContext } from '../../../contexts/useZoneContext';

const threatScoreHelpText = `Threat Score predicts the potential impact of a case by quantifying the unique impact of key factors and aggregating them into a single weighted score – a 0-100 numerical value that represents the severity of a case. The weighting of each factor is based on outputs of machine learning (ML) algorithms for the respective zone.

Note that for zones lacking sufficient data for an ML Prediction use the Basic Threat Score methodology which only considers the following factors and standard weightings:

•	50% Disease
•	25% Jurisdiction
•	15% Local Counsel
•	10% Plaintiffs Firm (combined plaintiffs firm logic)
`;

const predictedOutcomeHelpText = `ML Prediction is the predicted resolution value for a case based on machine learning (ML) algorithms.  This predicted value is intended to inform the attorneys decision-making and should be interpreted as directional and not a literal value, i.e. an ML Prediction = $9600 should be interpreted as low value - potentially a dismissal - as opposed to a literal  $9600 settlement value.

Checkmate models are trained specifically for each zone using the resolved cases for each respective zone; models are validated and/or calibrated each month, to include the most recent resolved cases in the training data set. We apply additional analytics to our ML models to determine the impact of each respective factor (case attribute) which is displayed in the Assessment to provide visibility into the ML Prediction. This is also used for weighting the values of each factor in the Threat Score calculation.`;

const bestFitCasesHelpText = `Checkmate applies analytics to determine the resolved cases that  are statistically most similar to the case being assessed.  The following factors are the most significant factors:

            • Disease
            • Jurisdiction
            • Plaintiffs Firm 
            • Local counsel

            Checkmate Assessment displays the top 5 “best fit cases” in priority order; if there are more than 5 or if priority ranks the same, then priority is based on most recent resolution date.`;

const caseStatisticsHelptText = `Reporting statistics calculated from Resolved cases with the same disease, plaintiffs firm and jurisdiction as the case being assessed.

If the case being assessed includes different local and national plaintiffs firms, then the subset of resolved cases used as reference for these statistics includes all cases that include either firm as local and/or national.  So if the case being assessed has Plaintiffs Firm = A and National Plaintiffs Firm = B, then all resolved cases that have Firm A or B as plaintiffs firm or national plaintiffs firm would be included in the reference case set.`;

export interface IAssessmentProps {
    expandedSections: string[] | undefined;
    isOpen: boolean;
}

export function Assessment(props: IAssessmentProps) {
    const { user } = useAuthContext();
    const [activeKey, setActiveKey] = React.useState<AccordionEventKey>(
        props.expandedSections ?? ['-1']
    );
    const caseFile = useCaseFileContext();
    const { data: hasModel, isLoading: hasModelIsLoading } = useGetAssessmentHasModel();
    const [hideCaseStatistics, setHideCaseStatistics] = React.useState<boolean>(false);
    const {zoneGuid} = useZoneContext();

    const handleAccordionItemSelectChange = (key: string) => {
        // convoluted logic here because AccordionEventKey can be a single value or an Array
        if (!key) {
            setActiveKey(['-1']);
        } else if (Array.isArray(activeKey)) {
            if (activeKey.some((ak) => ak === key)) {
                setActiveKey((prevKeys) => {
                    if (Array.isArray(prevKeys)) return prevKeys?.filter((pk) => pk !== key);
                    return prevKeys;
                });
            } else {
                setActiveKey([...activeKey, key]);
            }
        } else {
            setActiveKey(['-1', key]);
        }
    };

    const getSectionIsOpen = (idx: string) => {
        if (Array.isArray(activeKey)) {
            return activeKey?.some((key) => key === idx);
        } else {
            return activeKey === idx;
        }
    };

    const threatScoreValue = caseFile.currentCase.assessmentThreatScore?.toString();
    const predictedOutcomeValue = Common.formatCurrency(
        caseFile.currentCase.assessmentPredictedSettlementOutcome?.toString() ?? ''
    );

    const predictedOutcomeSecondaryTitle = hasModelIsLoading
        ? ''
        : hasModel
        ? predictedOutcomeValue
        : 'Not available due to insufficient data for zone.';

    const threatScoreHelpSubTitle = caseFile.currentCase.assessmentThreatScoreModifiedDate
        ? 'As of ' +
          Common.dateTimeFormat(caseFile.currentCase.assessmentThreatScoreModifiedDate) +
          ' Central'
        : undefined;

    React.useEffect(() => {
        if (props.isOpen) {
            setActiveKey(() => {
                const openIds = [
                    AssessmentSectionId.BestFitCases,
                    AssessmentSectionId.CaseStatistics,
                ];

                return openIds.filter((id) => (hideCaseStatistics && id !== AssessmentSectionId.CaseStatistics) || !hideCaseStatistics);
            });
        }
    }, [props.isOpen]);

    return (
        <Accordion activeKey={activeKey} flush alwaysOpen>
            {Authorization.userHasRight(UserRightsEnum.AssessmentThreatScoreGet, user, zoneGuid) && (
                <AssessmentAccordionItemWrapper
                    accordionKey={AssessmentSectionId.ThreatScore}
                    isOpen={getSectionIsOpen(AssessmentSectionId.ThreatScore)}
                    onSelectChange={handleAccordionItemSelectChange}
                    title="Threat Score"
                    secondaryTitle={hasModelIsLoading ? '' : threatScoreValue}
                    helpText={threatScoreHelpText}
                    helpTitle="Threat Score"
                    helpSubTitle={threatScoreHelpSubTitle}
                >
                    <div className="px-3 pb-3">
                        <ThreatScore
                            confidenceScore={caseFile.currentCase.assessmentThreatScoreConfidence}
                            isReady={getSectionIsOpen(AssessmentSectionId.ThreatScore)}
                        />
                    </div>
                </AssessmentAccordionItemWrapper>
            )}
            {Authorization.userHasRight(UserRightsEnum.AssessmentPredictedOutcomeGet, user, zoneGuid) && (
                <AssessmentAccordionItemWrapper
                    accordionKey={AssessmentSectionId.PredictedOutcome}
                    isOpen={getSectionIsOpen(AssessmentSectionId.PredictedOutcome)}
                    onSelectChange={handleAccordionItemSelectChange}
                    title="ML Prediction"
                    secondaryTitle={predictedOutcomeSecondaryTitle}
                    helpText={predictedOutcomeHelpText}
                    helpTitle="ML Prediction"
                    disableToggle={!hasModel}
                >
                    <div className="px-3 pb-3">
                        <PredictedOutcome
                            confidenceScore={
                                caseFile.currentCase.assessmentPredictedSettlementOutcomeConfidence
                            }
                            isReady={getSectionIsOpen(AssessmentSectionId.PredictedOutcome)}
                        />
                    </div>
                </AssessmentAccordionItemWrapper>
            )}
            {Authorization.userHasRight(UserRightsEnum.AssessmentBestFitCasesGet, user, zoneGuid) && (
                <AssessmentAccordionItemWrapper
                    accordionKey={AssessmentSectionId.BestFitCases}
                    isOpen={getSectionIsOpen(AssessmentSectionId.BestFitCases)}
                    onSelectChange={handleAccordionItemSelectChange}
                    title="Best Fit Cases"
                    helpText={bestFitCasesHelpText}
                    helpTitle="Best Fit Cases"
                >
                    <div className="px-3 pb-3">
                        <BestFitCases
                            isReady={getSectionIsOpen(AssessmentSectionId.BestFitCases)}
                        />
                    </div>
                </AssessmentAccordionItemWrapper>
            )}
            {Authorization.userHasRight(UserRightsEnum.AssessmentCaseStatisticsGet, user, zoneGuid) && (
                <AssessmentAccordionItemWrapper
                    accordionKey={AssessmentSectionId.CaseStatistics}
                    isOpen={
                        !hideCaseStatistics && getSectionIsOpen(AssessmentSectionId.CaseStatistics)
                    }
                    onSelectChange={handleAccordionItemSelectChange}
                    title="Resolved Cases with Same Jurisdiction | Plaintiffs Firm | Disease"
                    helpText={caseStatisticsHelptText}
                    helpTitle="Case Statistics"
                    suppressBottomBorder
                    secondaryTitle={!hideCaseStatistics ? '' : 'No matching cases found'}
                    disableToggle={hideCaseStatistics}
                >
                    <div className="px-3">
                        <CaseStatistics
                            isReady={getSectionIsOpen(AssessmentSectionId.CaseStatistics)}
                            onNoDataFound={() => {
                                setHideCaseStatistics(true);
                                setActiveKey((prev) => {
                                    if (isArray(prev)) {
                                        return prev.filter(
                                            (key) => key !== AssessmentSectionId.CaseStatistics
                                        );
                                    } else {
                                        return null;
                                    }
                                });
                            }}
                        />
                    </div>
                </AssessmentAccordionItemWrapper>
            )}
        </Accordion>
    );
}
